import React, { useState, useEffect } from 'react'
import {
    ArrowLeft,
    ArrowRight,
    Flag,
    CheckCircle,
    HelpCircle
} from 'lucide-react'
import KaTeXRenderer from '../common/KaTeXRenderer'
import TrueFalseQuestion from './TrueFalseQuestion'
import { imageExtractor } from '../../services/teacher/ImageExtractor'
import { EssayQuestionGrader } from './EssayQuestionGrader'
import { GradeResult } from '../../services/auto-coring/AutoCoringService'

interface QuizQuestionCardProps {
    question: any
    index: number
    totalQuestions: number
    displayNumber: number
    isDarkMode: boolean
    answer: any
    isFlagged: boolean
    showExplanation: boolean
    showQuestionList: boolean
    onAnswerChange: (questionId: string, value: any) => void
    onToggleFlag: (questionId: string) => void
    onToggleExplanation: (questionId: string) => void
    onPrevious: () => void
    onNext: () => void
    hasPrevious: boolean
    hasNext: boolean
    onUploadStatusChange?: (isUploading: boolean) => void
}

const QuizQuestionCard: React.FC<QuizQuestionCardProps> = ({
    question,
    index,
    totalQuestions,
    displayNumber,
    isDarkMode,
    answer,
    isFlagged,
    showExplanation,
    showQuestionList,
    onAnswerChange,
    onToggleFlag,
    onToggleExplanation,
    onPrevious,
    onNext,
    hasPrevious,
    hasNext,
    onUploadStatusChange
}) => {
    // Resolve image path - hỗ trợ cả teacher content và system paths
    const [questionImage, setQuestionImage] = useState<string | undefined>(undefined)
    const [explanationImage, setExplanationImage] = useState<string | undefined>(undefined)

    // Helper function: Encode image path để hỗ trợ khoảng trắng và ký tự tiếng Việt
    const encodeImagePath = (imagePath: string | undefined): string | undefined => {
        if (!imagePath) return undefined

        // Nếu là absolute URL (http/https) hoặc data URI (ảnh base64 nhúng thẳng từ PMaker),
        // trả về nguyên vẹn — encodeURIComponent từng phần sẽ phá hỏng chuỗi base64 (dấu ':' ';'
        // ',' và cả '/' xuất hiện trong base64 bị mã hoá/tách nhầm thành "đường dẫn file").
        if (imagePath.startsWith('http://') || imagePath.startsWith('https://') || imagePath.startsWith('data:')) {
            return imagePath
        }

        // Split path thành parts, encode từng part (trừ /)
        const parts = imagePath.split('/')
        const encodedParts = parts.map(part => {
            // Không encode phần rỗng hoặc chỉ có /
            if (!part || part === '') return part
            // Encode từng part để xử lý khoảng trắng và ký tự đặc biệt
            return encodeURIComponent(part)
        })

        return encodedParts.join('/')
    }

    useEffect(() => {
        const resolveImages = async () => {
            // Resolve question image
            if (question.hinh_anh) {
                const rawPath = question.hinh_anh
                // Nếu là teacher content path, resolve nó
                if (rawPath.startsWith('/teacher-content/') || rawPath.startsWith('teacher-content/')) {
                    try {
                        const resolved = await imageExtractor.resolveMediaSrc(rawPath)
                        setQuestionImage(resolved)
                    } catch (error) {
                        console.warn('⚠️ Failed to resolve teacher image path:', rawPath, error)
                        setQuestionImage(encodeImagePath(rawPath))
                    }
                } else {
                    setQuestionImage(encodeImagePath(rawPath))
                }
            } else {
                setQuestionImage(undefined)
            }

            // Resolve explanation image
            if (question.hinh_anh_giai_thich) {
                const rawPath = question.hinh_anh_giai_thich
                if (rawPath.startsWith('/teacher-content/') || rawPath.startsWith('teacher-content/')) {
                    try {
                        const resolved = await imageExtractor.resolveMediaSrc(rawPath)
                        setExplanationImage(resolved)
                    } catch (error) {
                        console.warn('⚠️ Failed to resolve teacher explanation image path:', rawPath, error)
                        setExplanationImage(encodeImagePath(rawPath))
                    }
                } else {
                    setExplanationImage(encodeImagePath(rawPath))
                }
            } else {
                setExplanationImage(undefined)
            }
        }
        resolveImages()
    }, [question.hinh_anh, question.hinh_anh_giai_thich])

    // Xử lý cả 3 cấu trúc dữ liệu (cũ, mới và bank)
    const questionType = question.loai || question.type

    // normalizeInline (gộp mọi '\n' thành dấu cách) CHỈ dùng cho PHƯƠNG ÁN (mỗi phương án 1
    // dòng). KHÔNG dùng cho CÂU DẪN nữa: câu dẫn giữ NGUYÊN số đoạn + vị trí bảng như file Word
    // (khớp live render PMaker). KaTeXRenderer đã tự bỏ '\n' sát cạnh công thức $...$ (giữ công
    // thức inline) rồi mới đổi '\n' đoạn thật thành xuống dòng — nên không cần gộp thô ở đây.
    const normalizeInline = (s: string) => s.replace(/\n+/g, ' ').trim()

    // Đối với câu hỏi đúng sai, chỉ sử dụng question_lead, bỏ cau_hoi (đã được map từ PMaker)
    const questionContent = (questionType === 'dung_sai' || questionType === 'true_false')
        ? undefined
        : (question.cau_hoi || question.cauHoi || question.question_lead || '')
    const questionOptions = question.lua_chon || question.luaChon
    const questionHints = question.goi_y || question.goiY

    // Helper to get True/False progress
    const getTrueFalseProgress = () => {
        if (!answer || typeof answer !== 'object' || Array.isArray(answer)) return 0
        const statementAnswers = Object.values(answer)
        return statementAnswers.filter(a => a !== null && a !== undefined).length
    }

    return (
        <div className={`quiz-question-card ${isDarkMode
            ? 'bg-gradient-to-br from-slate-800 via-slate-700 to-slate-800 border-slate-600'
            : 'bg-gradient-to-br from-white via-blue-50/30 to-indigo-50/30 border-blue-100/50'
            } rounded-xl sm:rounded-2xl md:rounded-3xl shadow-2xl ${isDarkMode ? 'p-3 sm:p-4 md:p-5' : 'p-2 sm:p-4 md:p-5'
            } border-2 backdrop-blur-sm w-full max-w-none`}>
            
            {/* PHẦN CÂU DẪN (PART DESCRIPTION) */}
            {question.partDescription && (
                <div className={`p-3 md:p-4 mb-4 rounded-xl border-l-4 shadow-sm ${isDarkMode ? 'bg-slate-700/80 border-blue-500 text-slate-200' : 'bg-blue-50/80 border-blue-600 text-slate-800'}`}>
                    {question.partTitle && (
                        <div className="font-bold text-base md:text-lg mb-2 flex items-center gap-2">
                            <span className="text-blue-600">📝</span> {question.partTitle}
                        </div>
                    )}
                    <div className="text-base md:text-lg leading-relaxed overflow-x-auto overflow-y-hidden [&_img]:max-w-full [&_img]:h-auto [&_img]:rounded-md [&_img]:shadow-sm">
                        <KaTeXRenderer content={question.partDescription} />
                    </div>
                </div>
            )}

            {/* Question Header - Improved Layout for Desktop Fullscreen */}
            <div className={`flex flex-col mb-1.5 md:mb-2 gap-2 quiz-question-header ${isDarkMode ? 'mt-4 md:mt-5' : 'mt-2'}`}>
                {/* Two-row layout for better space utilization */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 w-full">
                    {/* Left side - Question number */}
                    <h2 className={`quiz-question-title text-lg sm:text-xl md:text-2xl font-bold flex-shrink-0 ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                        Câu {displayNumber} / {totalQuestions}
                    </h2>

                    {/* Right side - Primary meta info (Difficulty, Type, Points) */}
                    <div className="flex items-center gap-1 sm:gap-1.5 md:gap-2 flex-shrink-0 mt-2 sm:mt-0">
                        {/* Difficulty Level */}
                        <span className={`px-1.5 py-0.5 sm:px-2 sm:py-0.5 rounded-full text-sm font-medium ${question.do_kho === 'M1' ? (isDarkMode ? 'bg-green-600 text-white' : 'bg-green-100 text-green-800') :
                            question.do_kho === 'M2' ? (isDarkMode ? 'bg-yellow-600 text-white' : 'bg-yellow-100 text-yellow-800') :
                                (isDarkMode ? 'bg-red-600 text-white' : 'bg-red-100 text-red-800')
                            }`}>
                            {question.do_kho || 'M1'}
                        </span>

                        {/* Question Type */}
                        <span className={`text-sm font-semibold px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-full border ${isDarkMode
                            ? 'bg-slate-700 text-slate-200 border-slate-600'
                            : 'bg-gradient-to-r from-blue-100 to-indigo-100 text-slate-600 border-blue-200'
                            }`}>
                            {questionType === 'dung_sai' || questionType === 'true_false' ? 'Đ/S' :
                                questionType === 'trac_nghiem_nhieu_lua_chon' || questionType === 'multiple_choice' ? 'TN' :
                                    questionType === 'tinh_toan' ? 'TLN' : 'TL'}
                        </span>

                        {/* Points */}
                        <span className={`text-sm font-bold px-2 py-0.5 sm:px-3 sm:py-1 rounded-full border ${isDarkMode
                            ? 'bg-slate-700 text-slate-200 border-slate-600'
                            : 'bg-gradient-to-r from-blue-100 to-indigo-100 text-slate-700 border-blue-200'
                            }`}>
                            {question.diem || 1}đ
                        </span>

                        {/* Show "Chưa chọn" / "Chưa trả lời" / Progress for True/False */}
                        {(questionType === 'trac_nghiem_nhieu_lua_chon' || questionType === 'multiple_choice') && !answer && (
                            <span className={`px-2 py-0.5 rounded-full text-sm font-medium ${isDarkMode
                                ? 'bg-amber-600 text-white'
                                : 'bg-amber-100 text-amber-800'
                                }`}>
                                Chưa chọn
                            </span>
                        )}

                        {(questionType === 'tinh_toan' || questionType === 'fill_blank') && !answer && (
                            <span className={`px-2 py-0.5 rounded-full text-sm font-medium ${isDarkMode
                                ? 'bg-amber-600 text-white'
                                : 'bg-amber-100 text-amber-800'
                                }`}>
                                Chưa trả lời
                            </span>
                        )}

                        {(questionType === 'dung_sai' || questionType === 'true_false') && (
                            <span className={`px-2 py-0.5 rounded-full text-sm font-medium ${isDarkMode
                                ? 'bg-blue-600 text-white'
                                : 'bg-blue-100 text-blue-800'
                                }`}>
                                {getTrueFalseProgress()}/4
                            </span>
                        )}

                        {/* Flag */}
                        <button
                            onClick={() => onToggleFlag(question.id)}
                            className={`quiz-action-btn p-1.5 sm:p-2 md:p-2.5 rounded-lg transition-all duration-300 ${isFlagged
                                ? 'bg-gradient-to-br from-orange-500 to-red-500 text-white shadow-orange-500/30'
                                : isDarkMode
                                    ? 'bg-slate-600 text-slate-300 hover:bg-slate-500'
                                    : 'bg-gradient-to-br from-slate-200 to-slate-300 text-slate-700 hover:from-slate-300 hover:to-slate-400 shadow-slate-500/20'
                                }`}
                            aria-label={isFlagged ? "Bỏ đánh dấu" : "Đánh dấu"}
                        >
                            <Flag className="w-3 h-3 sm:w-4 sm:h-4" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Question Content - Chỉ hiển thị cho câu hỏi không phải đúng sai - Mobile Optimized */}
            {!(questionType === 'dung_sai' || questionType === 'true_false') && questionContent && (
                <div className="mb-2 md:mb-2.5">
                    <div className={`text-base sm:text-lg md:text-xl font-medium leading-relaxed p-2 sm:p-2.5 md:p-3 rounded-lg md:rounded-xl border w-full ${isDarkMode
                        ? 'text-slate-200 bg-gradient-to-r from-slate-700 to-slate-600 border-slate-500'
                        : 'text-slate-800 bg-gradient-to-r from-slate-50 to-blue-50 border-slate-200'
                        }`}>
                        <KaTeXRenderer content={questionContent} moveImagesToEnd />
                    </div>
                </div>
            )}

            {/* Question Image - Chỉ hiển thị cho câu hỏi không phải đúng/sai */}
            {questionImage && !(questionType === 'dung_sai' || questionType === 'true_false') && (
                <div className="mb-2 w-full overflow-hidden">
                    <div className="p-1.5 sm:p-2 md:p-2.5 rounded-lg md:rounded-xl border shadow-sm w-full max-w-full box-border bg-white border-slate-200" style={{ maxWidth: '100%' }}>
                        <img
                            src={questionImage}
                            alt="Hình ảnh câu hỏi"
                            className="w-full h-auto max-h-[180px] sm:max-h-[200px] md:max-h-[220px] rounded-lg shadow-sm mx-auto block border border-slate-200 object-contain"
                            style={{ maxWidth: '100%', width: 'auto', display: 'block' }}
                            onError={(e) => {
                                console.warn('❌ Failed to load question image:', questionImage)
                                const target = e.currentTarget as HTMLImageElement
                                target.style.display = 'none'
                                // Show error message with path info
                                const errorDiv = document.createElement('div')
                                errorDiv.className = 'text-center text-slate-500 py-4 bg-gradient-to-r from-orange-50 to-orange-100 rounded-xl border-2 border-dashed border-orange-300'
                                errorDiv.innerHTML = `
                  <div class="text-xl mb-2">📷 Hình ảnh không khả dụng</div>
                  <div class="text-base text-slate-600 mb-2">File ảnh không tồn tại trong thư mục bank/Images/</div>
                  <div class="text-sm text-slate-400">Đường dẫn: ${questionImage}</div>
                  <div class="text-sm text-slate-400 mt-1">Ảnh sẽ được bổ sung trong phiên bản tiếp theo</div>
                `
                                target.parentNode?.appendChild(errorDiv)
                            }}
                            onLoad={() => {
                                console.log('✅ Question image loaded successfully:', questionImage)
                            }}
                        />
                    </div>
                </div>
            )}

            {/* Multiple Choice - Mobile Optimized */}
            {(questionType === 'trac_nghiem_nhieu_lua_chon' || questionType === 'multiple_choice') && questionOptions && (
                <div className="space-y-1 sm:space-y-1 md:space-y-1.5">
                    {questionOptions.map((option: any, optionIndex: number) => {
                        // Đề MỚI: option là {id, content} — option_id ổn định gửi làm giá trị chọn
                        // (không phụ thuộc vị trí/chữ cái). Đề CŨ: option là string thuần — vẫn
                        // dùng chữ cái A/B/C/D theo vị trí như trước (tương thích ngược).
                        const isObj = option && typeof option === 'object';
                        const optContent = isObj ? (option.content ?? option.text ?? '') : option;
                        const letter = String.fromCharCode(65 + optionIndex);
                        const optValue = isObj && option.id != null ? option.id : letter;
                        const isSelected = answer === optValue;
                        return (
                            <label
                                key={optValue || optionIndex}
                                className={`flex items-center space-x-2 md:space-x-2.5 p-1.5 sm:p-2 md:p-2.5 rounded-md sm:rounded-lg md:rounded-xl border-2 cursor-pointer transition-all duration-300 hover:scale-[1.01] shadow-md w-full ${isSelected
                                    ? 'bg-gradient-to-br from-blue-500 to-indigo-600 text-white border-blue-500 shadow-blue-500/30 transform scale-[1.02]'
                                    : isDarkMode
                                        ? 'bg-gradient-to-br from-slate-600 to-slate-700 text-slate-200 border-slate-500 hover:border-blue-400 hover:shadow-blue-500/20'
                                        : 'bg-gradient-to-br from-slate-50 to-slate-100 text-slate-800 border-slate-200 hover:border-blue-300 hover:shadow-blue-500/20'
                                    }`}
                            >
                                <input
                                    type="radio"
                                    name={`question-${question.id}`}
                                    value={optValue}
                                    checked={isSelected}
                                    onChange={(e) => onAnswerChange(question.id, e.target.value)}
                                    className="sr-only"
                                />
                                <div className={`w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 rounded-full flex items-center justify-center font-bold text-sm sm:text-base md:text-lg ${isSelected
                                    ? 'bg-white text-blue-600'
                                    : isDarkMode
                                        ? 'bg-slate-500 text-slate-200'
                                        : 'bg-slate-200 text-slate-600'
                                    }`}>
                                    {letter}
                                </div>
                                <div className="flex-1 text-lg sm:text-lg md:text-lg font-medium text-left">
                                    <KaTeXRenderer content={normalizeInline(optContent)} />
                                </div>
                                {isSelected && (
                                    <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 text-white" />
                                )}
                            </label>
                        );
                    })}
                </div>
            )}

            {/* True/False with Multiple Statements */}
            {(questionType === 'dung_sai' || questionType === 'true_false') && (
                <TrueFalseQuestion
                    question={{
                        id: question.id,
                        // PMaker đôi khi bỏ trống question_lead và ghi vào cau_hoi
                        question_lead: question.question_lead || question.cau_hoi || question.cauHoi || '',
                        lua_chon: questionOptions,
                        dap_an_dung: question.dap_an_dung,
                        // DS Schema fields for True/False questions
                        statement_a: question.statement_a,
                        statement_b: question.statement_b,
                        statement_c: question.statement_c,
                        statement_d: question.statement_d,
                        answer_a: question.answer_a,
                        answer_b: question.answer_b,
                        answer_c: question.answer_c,
                        answer_d: question.answer_d,
                        solution_a: question.solution_a,
                        solution_b: question.solution_b,
                        solution_c: question.solution_c,
                        solution_d: question.solution_d,
                        hinh_anh: question.hinh_anh,
                        statementOrder: question.statementOrder
                    }}
                    answers={{ [question.id]: answer }}
                    onAnswerChange={onAnswerChange}
                    showResults={false}
                    isFullscreen={isDarkMode}
                />
            )}

            {/* Fill in the blank */}
            {/* Fill in the blank */}
            {(questionType === 'tinh_toan' || questionType === 'fill_blank') && (
                <div className="space-y-4">
                    <div className="space-y-1.5 md:space-y-2">
                        {/* Instruction for decimal format - Hide for TLU */}
                        {question.examMetadata?.group !== 'TLU' && (
                            <div className={`border rounded-lg p-1.5 sm:p-2 md:p-2.5 ${isDarkMode
                                ? 'bg-slate-700 border-slate-500'
                                : 'bg-blue-50 border-blue-200'
                                }`}>
                                <p className={`text-sm md:text-base font-medium ${isDarkMode
                                    ? 'text-slate-200'
                                    : 'text-blue-700'
                                    }`}>
                                    💡 Hướng dẫn: Sử dụng dấu phẩy để ngăn cách phần nguyên và phần thập phân (ví dụ: 3,14)
                                </p>
                            </div>
                        )}

                        {/* Input cho câu hỏi điền đáp án (chỉ hiện cho TL, ẩn cho TLU) */}
                        {question.examMetadata?.group !== 'TLU' && (
                            <div className={`p-2 sm:p-2.5 md:p-3 rounded-lg md:rounded-xl border-2 ${isDarkMode
                                ? 'bg-gradient-to-r from-slate-700 to-slate-600 border-slate-500'
                                : 'bg-gradient-to-r from-slate-50 to-blue-50 border-slate-200'
                                }`}>
                                <input
                                    type="text"
                                    value={typeof answer === 'string' ? answer : (answer?.userAnswer || '')}
                                    onChange={(e) => onAnswerChange(question.id, e.target.value)}
                                    placeholder="Nhập câu trả lời của bạn..."
                                    className={`w-full p-2 sm:p-2.5 md:p-3 text-base md:text-lg border-2 rounded-lg md:rounded-xl focus:ring-2 transition-all duration-300 ${isDarkMode
                                        ? 'bg-slate-600 border-slate-400 text-slate-200 placeholder-slate-400 focus:border-blue-400 focus:ring-blue-500/20'
                                        : 'bg-white border-slate-300 text-slate-800 placeholder-slate-500 focus:border-blue-500 focus:ring-blue-500/20'
                                        }`}
                                />
                            </div>
                        )}
                    </div>

                    {/* AI Grading for Essay/Short Answer */}
                    {/* Chỉ hiện cho câu Tự luận (TLU) */}
                    {(question.examMetadata?.group === 'TLU') && (
                        <EssayQuestionGrader
                            key={question.id}
                            questionId={question.id}
                            questionContent={questionContent}
                            correctAnswer={question.dap_an_dung || question.loi_giai || ''}
                            isDarkMode={isDarkMode}
                            onUploadStatusChange={onUploadStatusChange}
                            // Pass persisted result if available
                            initialResult={typeof answer === 'object' && answer?.aiGrade ? answer.aiGrade : undefined}
                            onGradeComplete={(result) => {
                                // Khi chấm xong, update answer với kết quả từ AI
                                // Lưu ý: Cần update QuizScoringService để xử lý object này
                                onAnswerChange(question.id, {
                                    userAnswer: result.parts.map(p => p.content).join('\n'),
                                    aiGrade: result
                                });
                            }}
                        />
                    )}
                </div>
            )}

            {/* Hints */}
            {questionHints && questionHints.length > 0 && (
                <div className="mt-2 md:mt-3">
                    <button
                        onClick={() => onToggleExplanation(question.id)}
                        className={`flex items-center space-x-2 transition-colors duration-200 ${isDarkMode
                            ? 'text-blue-400 hover:text-blue-300'
                            : 'text-blue-600 hover:text-blue-800'
                            }`}
                    >
                        <HelpCircle className="w-4 h-4 md:w-5 md:h-5" />
                        <span className="font-semibold text-base md:text-lg">
                            {showExplanation ? 'Ẩn gợi ý' : 'Xem gợi ý'}
                        </span>
                    </button>
                    {showExplanation && (
                        <div className={`mt-2 p-2 sm:p-2.5 md:p-3 rounded-lg md:rounded-xl border-2 ${isDarkMode
                            ? 'bg-gradient-to-r from-slate-700 to-slate-600 border-slate-500'
                            : 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200'
                            }`}>
                            <h4 className={`font-semibold mb-1.5 md:mb-2 text-base md:text-lg ${isDarkMode
                                ? 'text-blue-300'
                                : 'text-blue-800'
                                }`}>Gợi ý:</h4>
                            <ul className="space-y-1.5 md:space-y-2">
                                {questionHints.map((hint: string, index: number) => (
                                    <li key={index} className={`flex items-start space-x-2 text-sm md:text-base ${isDarkMode
                                        ? 'text-blue-200'
                                        : 'text-blue-700'
                                        }`}>
                                        <span className={`mt-0.5 md:mt-1 ${isDarkMode
                                            ? 'text-blue-400'
                                            : 'text-blue-500'
                                            }`}>•</span>
                                        <span>{hint}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}

            {/* Navigation - Chỉ hiển thị khi ẩn danh sách câu hỏi (showQuestionList = false) */}
            {!showQuestionList && (
                <div className={`flex items-center justify-between pt-2 md:pt-3 mt-2 md:mt-3 border-t-2 ${isDarkMode ? 'border-slate-600' : 'border-slate-200'}`}>
                    <button
                        onClick={onPrevious}
                        disabled={!hasPrevious}
                        className={`flex items-center space-x-1 sm:space-x-2 px-2.5 py-1.5 sm:px-3 sm:py-2 rounded-lg transition-all duration-300 font-medium text-sm sm:text-base ${!hasPrevious
                            ? isDarkMode
                                ? 'bg-slate-700 text-slate-500 cursor-not-allowed'
                                : 'bg-slate-200 text-slate-500 cursor-not-allowed'
                            : isDarkMode
                                ? 'bg-blue-600 hover:bg-blue-500 text-white shadow-md hover:shadow-lg'
                                : 'bg-blue-500 hover:bg-blue-600 text-white shadow-md hover:shadow-lg'
                            }`}
                        title="Câu trước"
                    >
                        <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
                        <span className="hidden sm:inline">Trước</span>
                    </button>

                    <div className={`text-sm sm:text-base font-semibold ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        {displayNumber} / {totalQuestions}
                    </div>

                    <button
                        onClick={onNext}
                        disabled={!hasNext}
                        className={`flex items-center space-x-1 sm:space-x-2 px-2.5 py-1.5 sm:px-3 sm:py-2 rounded-lg transition-all duration-300 font-medium text-sm sm:text-base ${!hasNext
                            ? isDarkMode
                                ? 'bg-slate-700 text-slate-500 cursor-not-allowed'
                                : 'bg-slate-200 text-slate-500 cursor-not-allowed'
                            : isDarkMode
                                ? 'bg-blue-600 hover:bg-blue-500 text-white shadow-md hover:shadow-lg'
                                : 'bg-blue-500 hover:bg-blue-600 text-white shadow-md hover:shadow-lg'
                            }`}
                        title="Câu sau"
                    >
                        <span className="hidden sm:inline">Sau</span>
                        <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
                    </button>
                </div>
            )}

        </div>
    )
}

export default QuizQuestionCard
